import { useEffect, useCallback } from 'react';
import { useMIDIStore, usePlaybackStore } from '@/stores';
import type { MIDIDevice } from '@/types';
import { filterNotesByHand } from '@/utils/waterfallNoteState';

/**
 * Hook for handling MIDI device connection and input
 * Uses Web MIDI API to detect and receive events from MIDI keyboards
 */
export function useMIDIInput() {
  const { setSupported, setConnected, setDevices, selectDevice, noteOn, noteOff } = useMIDIStore();

  const { addPlayedNote, removePlayedNote, checkChordComplete } = usePlaybackStore();
  const config = usePlaybackStore((state) => state.config);
  const currentChord = usePlaybackStore((state) => state.currentChord);
  const advanceToNextChord = usePlaybackStore((state) => state.advanceToNextChord);

  // Handle MIDI message
  const handleMIDIMessage = useCallback(
    (event: MIDIMessageEvent) => {
      const [status, note, velocity] = event.data ?? new Uint8Array();

      // Note On: status 144-159 (0x90-0x9F)
      // Note Off: status 128-143 (0x80-0x8F) or Note On with velocity 0
      const isNoteOn = (status & 0xf0) === 0x90 && velocity > 0;
      const isNoteOff = (status & 0xf0) === 0x80 || ((status & 0xf0) === 0x90 && velocity === 0);

      if (isNoteOn) {
        noteOn(note, velocity / 127);
        addPlayedNote(note);

        // In wait mode, check if chord is complete. OTO-25: delegates to
        // filterNotesByHand (resolves assignedHand ?? staff via
        // getNoteHand()) instead of reading n.staff directly, so wait-mode
        // agrees with waterfall/expectedNotes/autoplay on which hand a
        // dynamically-reassigned note belongs to.
        if (config.waitMode && currentChord) {
          const expectedNotes = filterNotesByHand(currentChord.notes, config.hand).map(
            (n) => n.pitch
          );

          if (checkChordComplete(expectedNotes)) {
            // Small delay before advancing to next chord
            setTimeout(() => {
              advanceToNextChord();
            }, 100);
          }
        }
      } else if (isNoteOff) {
        noteOff(note);
        removePlayedNote(note);
      }
    },
    [
      noteOn,
      noteOff,
      addPlayedNote,
      removePlayedNote,
      config.waitMode,
      config.hand,
      currentChord,
      checkChordComplete,
      advanceToNextChord,
    ]
  );

  // Initialize MIDI
  useEffect(() => {
    // Check if Web MIDI is supported
    if (!navigator.requestMIDIAccess) {
      setSupported(false);
      console.warn(
        '[useMIDIInput/init] Failed: MIDI not supported | reason=navigator.requestMIDIAccess unavailable'
      );
      return;
    }

    setSupported(true);

    let midiAccess: MIDIAccess | null = null;
    const activeInputs: Map<string, MIDIInput> = new Map();

    const setupMIDI = async () => {
      try {
        midiAccess = await navigator.requestMIDIAccess({ sysex: false });
        setConnected(true);

        const updateDevices = () => {
          if (!midiAccess) return;

          const devices: MIDIDevice[] = [];

          midiAccess.inputs.forEach((input) => {
            devices.push({
              id: input.id,
              name: input.name || 'Unknown Device',
              manufacturer: input.manufacturer || 'Unknown',
              connected: input.state === 'connected',
            });

            // Set up message listener
            if (input.state === 'connected' && !activeInputs.has(input.id)) {
              input.onmidimessage = handleMIDIMessage;
              activeInputs.set(input.id, input);
            }
          });

          setDevices(devices);

          // Auto-select first device if none selected
          if (devices.length > 0) {
            const store = useMIDIStore.getState();
            if (!store.selectedDeviceId) {
              selectDevice(devices[0].id);
            }
          }
        };

        // Get initial device list
        updateDevices();

        if (midiAccess) {
          midiAccess.onstatechange = () => {
            updateDevices();
          };
        }
      } catch (err) {
        console.error('[useMIDIInput/setupMIDI] Failed: MIDI access denied | reason=', err);
        setConnected(false);
      }
    };

    setupMIDI();

    return () => {
      // Clean up listeners
      activeInputs.forEach((input) => {
        input.onmidimessage = null;
      });
      activeInputs.clear();

      if (midiAccess) {
        midiAccess.onstatechange = null;
      }
    };
  }, [setSupported, setConnected, setDevices, selectDevice, handleMIDIMessage]);
}
